import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Newspaper, ArrowLeftRight, Check, Lock } from "lucide-react";
import { Card } from "@/components/primitives";
import api from "@/lib/api";

const PRODUCTS = [
  [ShieldCheck, "The Registry", "Every diamond and precious-metals asset is entered into a permanent registry record, origin mine, weight, and the four Cs, captured at source. Independent verification against producer, tender, and Kimberley Process data. A Virtus certificate of provenance is a premium, tamper-evident credential recognized across the trade.", "Explore the Registry →", "/verify"],
  [Newspaper, "The VDR Daily Wire", "Daily market intelligence for the American natural commodities trade. US retail and wholesale desks, bridal, provenance, precious metals, strategic minerals, and global FX. Delivered before US markets open.", "Read today's Wire →", "/wire"],
  [ArrowLeftRight, "Trade Center", "A verified, KYC-aligned, Kimberley-Process-compliant marketplace for rough and polished diamonds. Offer, counter, agree, sign, pay, track. All trades settle in USD via SWIFT under US-law-governed master agreements. WFDB arbitration available.", "Enter the Trade Center →", "/trade-center"],
];

export function ThreeProducts() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-products">
      <h2 className="font-serif text-3xl sm:text-5xl tracking-tight mb-10">Three products. One source of truth.</h2>
      <div className="grid md:grid-cols-3 gap-5">
        {PRODUCTS.map(([Icon, t, d, cta, to]) => (
          <Card key={t} className="p-8 flex flex-col">
            <Icon size={22} className="text-bb-gold" />
            <h3 className="font-serif text-2xl mt-4">{t}</h3>
            <p className="text-bb-muted text-sm leading-relaxed mt-3 flex-1">{d}</p>
            <button onClick={() => nav(to)} className="mt-5 text-left text-bb-gold text-[0.72rem] uppercase tracking-[0.14em] font-semibold hover:underline underline-offset-4">{cta}</button>
          </Card>
        ))}
      </div>
    </section>
  );
}

const ORIGINS = [
  ["🇧🇼", "Botswana", "Jwaneng, Orapa, Letlhakane. World's leading gem-quality producer by value."],
  ["🇳🇦", "Namibia", "Namdeb, Debmarine Namibia. Premium marine and onshore production."],
  ["🇿🇦", "South Africa", "Venetia, Cullinan, Finsch. Historic producer, active supply-discipline story."],
  ["🇦🇴", "Angola", "Catoca, Luaxe. Africa's second-largest producer by value."],
  ["🇨🇦", "Canada", "Diavik, Ekati, Gahcho Kué. Northern-origin premium buyers value."],
  ["🇦🇺", "Australia", "Argyle legacy, colored stones. Fancy-color heritage."],
  ["🇸🇱", "Sierra Leone", "KP-certified rough. Artisanal and industrial."],
  ["🇨🇩", "DR Congo", "KP-certified rough. Volume producer."],
  ["🇷🇺", "Russia (restricted)", "Coverage under G7 sanctions and OFAC restrictions. Compliance-only."],
  ["🌐", "Mixed-origin lots", "Tracked with provenance flags."],
];

export function OriginsGrid() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-origins">
      <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">Global origin coverage. American source of truth.</h2>
      <p className="text-bb-muted text-sm mt-3 max-w-2xl">Virtus registers, verifies, and tracks natural stones and precious metals from every major producing jurisdiction. One registry. One index. Multiple origins.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
        {ORIGINS.map(([flag, name, d]) => (
          <button key={name} onClick={() => nav("/wire")} data-testid={`origin-${name.split(" ")[0].toLowerCase()}`}
            className="text-left border border-white/8 hover:border-bb-gold/40 transition-colors rounded-sm bg-bb-surface/40 p-4">
            <div className="text-lg">{flag}</div>
            <div className="font-serif text-[1.02rem] mt-1">{name}</div>
            <p className="text-bb-muted text-xs leading-relaxed mt-1">{d}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

const DESKS = [
  ["For US retailers", "POS trends, bridal share, natural vs lab-grown pricing, origin-story assets for the shop floor.", "See the retailer view →", "/for-retailers"],
  ["For US wholesalers", "RAPI, Tenoris panel data, memo tracking, 47th Street and JCK signals, dealer sentiment.", "See the wholesaler view →", "/for-wholesalers"],
  ["For US importers", "CBP country-of-mining, OFAC and G7 sanctions guidance, KP updates, USKPA licensee changes, tariff moves.", "See the importer view →", "/compliance"],
  ["For institutional investors", "VDR-RDI, producer earnings, mine-level production, fiscal revenue trackers, M&A tracking.", "See the investor view →", "/intelligence"],
];

export function DeskCards() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-desks">
      <h2 className="font-serif text-3xl sm:text-5xl tracking-tight mb-10">Built for the desks that make daily decisions.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {DESKS.map(([t, d, cta, to]) => (
          <div key={t} className="border border-white/8 rounded-sm bg-bb-surface/40 p-6">
            <h3 className="font-serif text-xl">{t}</h3>
            <p className="text-bb-muted text-sm leading-relaxed mt-2">{d}</p>
            <button onClick={() => nav(to)} className="mt-4 text-bb-gold text-[0.7rem] uppercase tracking-[0.12em] font-semibold hover:underline underline-offset-4">{cta}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

const TIERS = [
  ["VDR Wire", "$49", "/ month", "Billed annually, two months free. Switch to monthly anytime.",
   ["Daily Wire, every desk, weekday delivery", "US markets pre-open", "Full VDR-RDI level and daily commentary", "Global FX, metals, strategic minerals"], "Subscribe", "/pricing", false],
  ["VDR Wire Pro", "$499", "/ month", "Billed annually, two months free.",
   ["Everything in VDR Wire", "US Compliance Desk PDF (weekly)", "Historical archive access", "Spreadsheet exports", "Priority support"], "Subscribe", "/pricing", true],
  ["Enterprise / Institutional", "Custom", " pricing", "Registry API, data feeds, bespoke research.",
   ["Registry API access", "VDR-RDI data feed", "Custom dashboards", "Bespoke research", "Multi-seat licensing"], "Contact sales", "mailto:virtusregistry@virturegistry.com", false],
];

export function PricingTiers() {
  const nav = useNavigate();
  const go = (to) => (to.startsWith("mailto:") ? window.location.assign(to) : nav(to));
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-pricing">
      <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">Pricing</h2>
      <p className="text-bb-muted text-sm mt-2">Public tiers. Institutional plans on request.</p>
      <div className="grid md:grid-cols-3 gap-5 mt-8">
        {TIERS.map(([name, price, per, note, feats, cta, to, featured]) => (
          <div key={name} className={`rounded-sm p-7 flex flex-col bg-bb-surface/40 border ${featured ? "border-bb-gold/60" : "border-white/8"}`} data-testid={`tier-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
            <h3 className="font-serif text-2xl">{name}</h3>
            <div className="mt-3"><span className="font-serif text-4xl text-bb-gold tnum">{price}</span><span className="text-bb-dim text-sm">{per}</span></div>
            <p className="text-bb-muted text-xs mt-1">{note}</p>
            <ul className="mt-5 space-y-2 flex-1">
              {feats.map((f) => <li key={f} className="flex gap-2 text-sm text-bb-text/85"><Check size={14} className="text-bb-gold shrink-0 mt-0.5" />{f}</li>)}
            </ul>
            <button onClick={() => go(to)} className={`mt-6 rounded-sm px-5 py-3 text-[0.74rem] font-semibold uppercase tracking-[0.08em] transition-colors ${featured ? "bg-bb-gold text-bb-bg hover:bg-bb-champagne" : "border border-bb-gold text-bb-gold hover:bg-bb-gold/10"}`}>{cta}</button>
          </div>
        ))}
      </div>
      <p className="text-bb-dim text-[0.66rem] mt-6 uppercase tracking-[0.1em]">SOC 2 roadmap in progress · KYC/AML aligned · OFAC screening · Kimberley Process aligned · US-law-governed</p>
    </section>
  );
}

const COMPLIANCE_POINTS = [
  "US-headquartered, US-law-governed contracts", "KYC and AML controls on all Trade Center parties",
  "OFAC and G7 sanctions screening", "Kimberley Process alignment", "USKPA-aware workflows",
  "WFDB arbitration option on all trades", "All trades settled in USD via SWIFT",
  "CBP country-of-mining data collection ready",
];

export function ComplianceTrust() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-compliance">
      <div className="grid lg:grid-cols-2 gap-10 border border-white/8 rounded-sm bg-bb-surface/30 p-8 sm:p-12">
        <div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Built US-compliant from day one.</h2>
          <button onClick={() => nav("/compliance")} className="mt-6 text-bb-gold text-[0.72rem] uppercase tracking-[0.14em] font-semibold hover:underline underline-offset-4">Read our full compliance approach →</button>
        </div>
        <ul className="space-y-2.5">
          {COMPLIANCE_POINTS.map((p) => <li key={p} className="flex gap-2.5 text-sm text-bb-text/85"><Check size={15} className="text-bb-gold shrink-0 mt-0.5" />{p}</li>)}
        </ul>
      </div>
    </section>
  );
}

const HZ_COLS = [["7d", "7 days"], ["1m", "1 month"], ["6m", "6 months"], ["12m", "12 months"]];

function ForecastCell({ h }) {
  if (!h) {
    return (
      <td className="px-2 py-3 text-right">
        <Lock size={12} className="text-bb-gold/60 inline-block" />
      </td>
    );
  }
  const up = h.change_pct >= 0;
  return (
    <td className="px-2 py-3 text-right whitespace-nowrap">
      <span className="tnum text-bb-text/90">{Number(h.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
      <span className={`tnum ml-1.5 text-[0.68rem] ${up ? "text-bb-success" : "text-bb-error"}`}>{up ? "+" : ""}{h.change_pct}%</span>
    </td>
  );
}

export function ForecastBoard() {
  const nav = useNavigate();
  const [fc, setFc] = useState(null);
  useEffect(() => { api.get("/public/forecasts").then(({ data }) => setFc(data)).catch(() => {}); }, []);
  const rows = [...(fc?.diamonds || []), ...(fc?.commodities || [])];
  if (!rows.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-forecast-board">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[0.62rem] uppercase tracking-[0.3em] text-bb-gold">Research Desk</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2">Commodity projections</h2>
          <p className="text-bb-muted text-sm mt-2 max-w-2xl">Modeled 7-day, 1-month, 6-month and 12-month projections from the VDR research model. Research context only, not investment advice.</p>
        </div>
        <button onClick={() => nav("/market?tab=projections")} data-testid="forecast-full-cta"
          className="text-bb-gold text-[0.7rem] uppercase tracking-[0.12em] font-semibold hover:underline underline-offset-4">
          Full projections & ranges →
        </button>
      </div>
      <div className="border border-white/8 rounded-sm bg-bb-surface/40 mt-6 overflow-x-auto">
        <table className="w-full text-[0.82rem] min-w-[760px]">
          <thead>
            <tr className="text-bb-dim text-[0.56rem] uppercase tracking-[0.12em]">
              <th className="text-left font-medium px-4 py-3">Commodity</th>
              <th className="text-right font-medium px-2 py-3">Current</th>
              {HZ_COLS.map(([k, l]) => <th key={k} className="text-right font-medium px-2 py-3">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.symbol} className="border-t border-white/5" data-testid={`forecast-row-${r.symbol.toLowerCase()}`}>
                <td className="px-4 py-3">
                  <span className="text-bb-text">{r.label}</span>
                  <span className="text-bb-dim text-[0.62rem] ml-2">{r.unit === "index" ? "index" : `$/${r.unit}`}</span>
                </td>
                <td className="px-2 py-3 text-right tnum text-bb-gold whitespace-nowrap">
                  {r.unit === "index" ? Number(r.current).toLocaleString() : `$${Number(r.current).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                </td>
                {HZ_COLS.map(([k]) => <ForecastCell key={k} h={r.horizons?.[k]} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {fc?.access === "teaser" && (
        <div className="border border-bb-gold/40 bg-bb-gold/[0.05] rounded-sm p-4 mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="home-forecast-unlock">
          <p className="text-bb-text/85 text-xs">The 7-day view is free. Members see 1, 6 and 12-month projections with full statistical ranges.</p>
          <button onClick={() => nav("/pricing")} data-testid="home-forecast-unlock-cta"
            className="bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm px-4 py-2 text-[0.66rem] font-semibold uppercase tracking-[0.08em]">
            Unlock · Wire from $49/mo
          </button>
        </div>
      )}
      <p className="text-bb-dim text-[0.62rem] mt-3 leading-relaxed max-w-4xl">{fc?.disclaimer}</p>
    </section>
  );
}

export function LatestEdition() {
  const nav = useNavigate();
  const [ed, setEd] = useState(null);
  useEffect(() => { api.get("/public/provenance").then(({ data }) => setEd(data)).catch(() => {}); }, []);
  if (!ed) return null;
  const excerpt = (ed.long_read?.body || ed.dek || "").split("\n\n")[0].slice(0, 320);
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-latest-edition">
      <div className="border border-bb-gold/30 rounded-sm bg-bb-surface/40 p-8 sm:p-10">
        <div className="text-[0.62rem] uppercase tracking-[0.3em] text-bb-gold" data-testid="latest-edition-line">
          The Provenance · Edition {String(ed.edition_no).padStart(3, "0")} · {ed.edition_date}
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight max-w-3xl">{ed.title}</h2>
        <p className="text-bb-secondary text-sm leading-relaxed mt-4 max-w-3xl">{excerpt}…</p>
        <button onClick={() => nav("/wire")} data-testid="latest-edition-cta"
          className="mt-6 text-bb-champagne text-[0.72rem] uppercase tracking-[0.12em] font-semibold hover:underline underline-offset-4">
          Read the full edition →
        </button>
      </div>
    </section>
  );
}

export function HomeBoard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/ticker-rail").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data?.items?.length) return null;
  const want = ["Gold", "Silver", "Platinum", "Palladium", "Copper", "VDR-RDI"];
  const cards = want.map((w) => data.items.find((i) => i.label === w)).filter(Boolean);
  const fx = data.items.filter((i) => i.label.startsWith("USD/"));
  const ts = data.as_of ? new Date(data.as_of).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET" : "";
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-board">
      <h2 className="font-serif text-3xl sm:text-4xl">The Board</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
        {cards.map((c) => (
          <div key={c.label} className="border border-white/8 rounded-sm bg-bb-surface/40 p-4" data-testid={`board-card-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-bb-dim text-[0.6rem] uppercase tracking-[0.14em]">{c.label}</div>
            <div className="font-serif text-xl tnum mt-1">{c.value}{c.unit ? <span className="text-bb-dim text-[0.6rem] ml-1">{c.unit}</span> : null}</div>
            {c.chg != null && <div className={`tnum text-[0.68rem] mt-0.5 ${c.chg >= 0 ? "text-bb-success" : "text-bb-error"}`}>{c.chg > 0 ? "+" : ""}{c.chg}%</div>}
            <div className="text-bb-dim text-[0.54rem] mt-1">as of {ts}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-4 text-[0.72rem]" data-testid="board-fx-row">
        {fx.map((f) => (
          <span key={f.label} className="tnum text-bb-secondary">{f.label} <span className="text-bb-text">{f.value}</span>
            {f.chg != null && <span className={`ml-1 ${f.chg >= 0 ? "text-bb-success" : "text-bb-error"}`}>{f.chg > 0 ? "+" : ""}{f.chg}%</span>}
          </span>
        ))}
      </div>
      <button onClick={() => nav("/market")} data-testid="board-full-cta"
        className="mt-5 text-bb-champagne text-[0.72rem] uppercase tracking-[0.12em] font-semibold hover:underline underline-offset-4">
        Full market data →
      </button>
    </section>
  );
}

export function LiveDeskBadge() {
  const [desk, setDesk] = useState(null);
  useEffect(() => { api.get("/public/desk-status").then(({ data }) => setDesk(data)).catch(() => {}); }, []);
  if (!desk) return null;
  return desk.execution_live ? (
    <span className="inline-flex items-center gap-2 border border-bb-success/40 text-bb-success rounded-sm px-3 py-1.5 text-[0.62rem] uppercase tracking-[0.16em]" data-testid="live-desk-badge">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bb-success opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-bb-success" />
      </span>
      Live Desk · Brokerage connected{desk.env === "paper" ? " · dry-run" : ""}
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 border border-white/15 text-bb-muted rounded-sm px-3 py-1.5 text-[0.62rem] uppercase tracking-[0.16em]" data-testid="live-desk-badge">
      Simulated execution
    </span>
  );
}

export function SellCards() {
  const nav = useNavigate();
  const cards = [
    ["The Provenance", "Twice-monthly commodity intelligence: seven ranked stories, the board, the long read. Published on the 1st and the 15th.", "from $29/mo", "Subscribe", "/pricing", false],
    ["Virtus Trade", "Trade US equities from your VDR terminal. Orders route through the Virtus desk brokerage with one-click execution from the day's top setups.", "2% per trade", "Start trading", "/register", true],
    ["Metals Desk", "Daily gold, platinum and silver briefings with composite spot, production data and central bank flows.", "$2,500/yr", "Learn more", "/metals-desk", false],
    ["VDR-RDI Data License", "The Virtus rough diamond index as an institutional data feed with methodology and revision history.", "Institutional", "Contact sales", "mailto:virtusregistry@virturegistry.com", false],
  ];
  const go = (to) => (to.startsWith("mailto:") ? window.location.assign(to) : nav(to));
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-sell-cards">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([t, d, price, cta, to, liveBadge]) => (
          <div key={t} className="border border-white/8 rounded-sm bg-bb-surface/40 p-7 flex flex-col" data-testid={`sell-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <h3 className="font-serif text-2xl">{t}</h3>
            {liveBadge && <div className="mt-2.5"><LiveDeskBadge /></div>}
            <p className="text-bb-muted text-sm leading-relaxed mt-3 flex-1">{d}</p>
            <div className="text-bb-gold font-serif text-lg mt-4 tnum">{price}</div>
            <button onClick={() => go(to)} className="mt-3 border border-bb-gold text-bb-champagne hover:bg-bb-gold/10 transition-colors rounded-sm px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.08em]">
              {cta} →
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function RegistryStrip() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24 text-center" data-testid="home-registry-strip">
      <h2 className="font-serif text-3xl sm:text-4xl">One registry across five commodity classes</h2>
      <div className="flex flex-wrap justify-center gap-3 mt-7">
        {[["VDR", "Diamond"], ["VGR", "Gold"], ["VPR", "Platinum"], ["VSR", "Silver"], ["VCR", "Critical Minerals"]].map(([c, n]) => (
          <div key={c} className="border border-white/8 rounded-sm bg-bb-surface/40 px-6 py-4">
            <div className="font-serif text-xl text-bb-gold">{c}</div>
            <div className="text-bb-dim text-[0.6rem] uppercase tracking-[0.12em] mt-0.5">{n}</div>
          </div>
        ))}
      </div>
      <p className="text-bb-muted text-sm mt-6 max-w-2xl mx-auto">
        Every asset registered with Virtus receives a permanent VDR passport with full provenance,
        chain-of-custody, and independent verification.
      </p>
      <button onClick={() => nav("/registry")} data-testid="registry-strip-cta"
        className="mt-5 text-bb-champagne text-[0.72rem] uppercase tracking-[0.12em] font-semibold hover:underline underline-offset-4">
        Verify an asset →
      </button>
    </section>
  );
}

export function MethodologyStrip() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-methodology">
      <div className="grid sm:grid-cols-3 gap-px bg-bb-line border border-bb-line">
        {[["62", "network sources"], ["5-tier", "data hierarchy"], ["AI-curated", "human-edited"]].map(([v, l]) => (
          <div key={l} className="bg-bb-bg p-8 text-center">
            <div className="font-serif text-3xl text-bb-gold">{v}</div>
            <div className="text-bb-muted text-xs uppercase tracking-[0.14em] mt-2">{l}</div>
          </div>
        ))}
      </div>
      <div className="text-center mt-5">
        <button onClick={() => nav("/vdr-rdi")} data-testid="methodology-cta"
          className="text-bb-champagne text-[0.72rem] uppercase tracking-[0.12em] font-semibold hover:underline underline-offset-4">
          Read our methodology →
        </button>
      </div>
    </section>
  );
}

export function CollectionStrip() {
  const nav = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-5 pb-24" data-testid="home-collection-strip">
      <div className="border border-white/8 rounded-sm bg-bb-surface/40 p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[0.6rem] uppercase tracking-[0.24em] text-bb-gold">The Collection</div>
          <p className="text-bb-secondary text-sm mt-1.5">Explore The Collection: registered provenance objects, beginning with the Botswana Pride Medallion.</p>
        </div>
        <button onClick={() => nav("/collection")} data-testid="collection-strip-cta"
          className="border border-bb-gold text-bb-champagne hover:bg-bb-gold/10 transition-colors rounded-sm px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.08em]">
          See The Collection →
        </button>
      </div>
    </section>
  );
}

export function FinalCta() {
  const nav = useNavigate();
  return (
    <section className="border-t border-white/5 bg-[#0F2145] py-20 text-center px-5" data-testid="home-final-cta">
      <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#F5F7FB]">Read the market like a registry.</h2>
      <p className="text-[#9FB0CF] text-base mt-4">The Provenance publishes on the 1st and the 15th of every month. Full access from $29/month.</p>
      <button onClick={() => nav("/wire")} data-testid="final-cta-btn"
        className="mt-8 bg-bb-gold text-[#F5F7FB] hover:bg-bb-champagne transition-colors rounded-sm px-9 py-3.5 text-[0.8rem] font-semibold uppercase tracking-[0.06em]">
        Start your subscription
      </button>
      <p className="text-[#7186A9] text-xs mt-4">Two months free with annual billing. Cancel anytime.</p>
    </section>
  );
}
